import { useEffect, useState } from "react";
import { MapPin, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import { useMode } from "@/lib/mode";
import { buildGeoCopy, resolveUserCity, type GeoCopy } from "@/lib/marketing/dynamicGeoCopy";
import { useLanguage } from "@/hooks/useLanguage";

async function recordGeoEvent(copy: GeoCopy, clicked: boolean) {
  try {
    await supabase.from("geo_marketing_events").insert({
      session_id: getSessionId(),
      tenant_id: "univision-demo",
      city: copy.city,
      message: copy.message,
      clicked,
      metadata: { mode: copy.isBetting ? "betting" : "media" } as never,
    });
  } catch (e) {
    console.warn("[geoBanner] insert failed", e);
  }
}

export function GeoDynamicBanner() {
  const [mode] = useMode();
  const { lang, t } = useLanguage();
  const [copy, setCopy] = useState<GeoCopy | null>(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    let alive = true;
    void resolveUserCity().then((city) => {
      if (!alive) return;
      const c = buildGeoCopy(city, mode, lang);
      setCopy(c);
      void trackEvent("geo_banner_viewed", { city: c.city, mode });
      void recordGeoEvent(c, false);
    });
    return () => {
      alive = false;
    };
  }, [mode, lang]);

  if (!copy || closed) return null;

  const handleClick = () => {
    void trackEvent("geo_banner_clicked", { city: copy.city, mode });
    void recordGeoEvent(copy, true);
  };

  return (
    <div
      className={`relative flex items-center gap-3 rounded-2xl border p-3 text-[12px] ${
        copy.isBetting
          ? "border-[oklch(0.82_0.24_145)]/30 bg-[oklch(0.82_0.24_145)]/5"
          : "border-border glass"
      }`}
    >
      <MapPin
        className={`h-4 w-4 shrink-0 ${
          copy.isBetting ? "text-[oklch(0.82_0.24_145)]" : "text-primary"
        }`}
      />
      <div className="min-w-0 flex-1">
        <div className="font-bold leading-snug">{copy.message}</div>
        {copy.isBetting && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            <span className="rounded-full border border-[oklch(0.82_0.24_145)]/40 bg-[oklch(0.82_0.24_145)]/10 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-[oklch(0.82_0.24_145)]">
              {t("geo_simulated")}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {t("geo_responsible")}
            </span>
          </div>
        )}
      </div>
      {/* MOBILE UX DEDUP — dead "Saber más" CTA removed for media mode (no
          destination). Only the betting offer CTA, which represents the
          offer itself, is kept. */}
      {copy.isBetting && (
        <button
          onClick={handleClick}
          className="rounded-lg bg-[oklch(0.82_0.24_145)] px-3 py-1.5 text-[11px] font-bold text-background transition"
        >
          {t("geo_cta_offer")}
        </button>
      )}
      <button
        onClick={() => setClosed(true)}
        aria-label={t("common_close")}
        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}